import type { LucideIcon } from 'lucide-react';
import {
    forwardRef,
    useImperativeHandle,
    useRef,
    type InputHTMLAttributes,
    type KeyboardEvent,
} from 'react';

import { cn } from '../lib/cn';

export interface AutocompleteInputProps
    extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
    value: string;
    onChange: (value: string) => void;
    /** Candidate completions. The first one that case-insensitively starts with
     *  the current value (and is longer) renders inline as greyed "ghost" text
     *  right after the caret — no dropdown. */
    suggestions: string[];
    /** Enter pressed. Receives the current typed value (NOT the ghost — accept
     *  the ghost first with Tab / ArrowRight if you want it). */
    onCommit?: (value: string) => void;
    icon?: LucideIcon;
    className?: string;
}

// A text input with an inline ghost completion, the way an editor or the
// browser address bar completes: the matched suffix renders greyed right after
// the caret, and Tab / ArrowRight (at the end of the field) accept it. Generic
// and presentational — the caller supplies `suggestions`; how they're sourced
// (static list, debounced query, ...) is the caller's business.
export const AutocompleteInput = forwardRef<HTMLInputElement, AutocompleteInputProps>(
    (
        { value, onChange, suggestions, onCommit, icon: Icon, className, disabled, onKeyDown, ...props },
        ref,
    ) => {
        const innerRef = useRef<HTMLInputElement>(null);
        useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

        const completion =
            value.trim().length > 0
                ? suggestions.find(
                      (s) => s.length > value.length && s.toLowerCase().startsWith(value.toLowerCase()),
                  )
                : undefined;
        const ghost = completion ? completion.slice(value.length) : '';

        const caretAtEnd = () => {
            const el = innerRef.current;
            return !!el && el.selectionStart === value.length && el.selectionEnd === value.length;
        };

        const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
            onKeyDown?.(e);
            if (e.defaultPrevented) return;
            if (completion && (e.key === 'Tab' || (e.key === 'ArrowRight' && caretAtEnd()))) {
                e.preventDefault();
                onChange(completion);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                onCommit?.(value);
            }
        };

        const pad = Icon ? 'pl-9 pr-3' : 'px-3';

        return (
            <div
                className={cn(
                    'relative flex h-10 w-full items-center rounded-md border border-solid border-gray-300 bg-white focus-within:border-transparent focus-within:ring-2 focus-within:ring-blue-500 dark:border-gray-600 dark:bg-gray-800',
                    disabled && 'cursor-not-allowed opacity-50',
                    className,
                )}
            >
                {Icon && (
                    <Icon
                        aria-hidden="true"
                        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    />
                )}
                {/* Ghost overlay: an invisible copy of the typed text reserves the
                    exact caret position so the grey suffix lines up after it. */}
                <div
                    aria-hidden="true"
                    className={cn(
                        'pointer-events-none absolute inset-0 flex items-center overflow-hidden text-sm',
                        pad,
                    )}
                >
                    <span className="invisible whitespace-pre">{value}</span>
                    <span className="whitespace-pre text-gray-400 dark:text-gray-500">{ghost}</span>
                </div>
                <input
                    ref={innerRef}
                    type="text"
                    value={value}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className={cn(
                        'h-full w-full bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed dark:text-gray-100 dark:placeholder:text-gray-500',
                        pad,
                    )}
                    {...props}
                />
            </div>
        );
    },
);
AutocompleteInput.displayName = 'AutocompleteInput';
